import * as React from 'react'

import { cn, definedStyles } from './cn'

/** MUI spacing unit (1 unit = 8px). */
const space = (v: number | string | undefined) =>
  typeof v === 'number' ? `${v * 8}px` : v

export interface BoxProps extends React.HTMLAttributes<HTMLDivElement> {
  component?: React.ElementType
  display?: React.CSSProperties['display']
  flexDirection?: React.CSSProperties['flexDirection']
  alignItems?: React.CSSProperties['alignItems']
  justifyContent?: React.CSSProperties['justifyContent']
  flexWrap?: React.CSSProperties['flexWrap']
  flexGrow?: React.CSSProperties['flexGrow']
  flexShrink?: React.CSSProperties['flexShrink']
  gap?: number | string
  width?: number | string
  height?: number | string
  maxWidth?: number | string
  minWidth?: number | string
  maxHeight?: number | string
  minHeight?: number | string
  overflow?: React.CSSProperties['overflow']
  position?: React.CSSProperties['position']
  textAlign?: React.CSSProperties['textAlign']
  alignSelf?: React.CSSProperties['alignSelf']
  /** Forwarded when rendering as a form. */
  noValidate?: boolean
  m?: number | string
  mt?: number | string
  mb?: number | string
  ml?: number | string
  mr?: number | string
  mx?: number | string
  my?: number | string
  p?: number | string
  pt?: number | string
  pb?: number | string
  pl?: number | string
  pr?: number | string
  px?: number | string
  py?: number | string
}

/**
 * Layout div retained for converted MUI Box call sites; prefer a plain
 * div with Tailwind classes in new code.
 */
const Box = React.forwardRef<HTMLDivElement, BoxProps>(
  (
    {
      component: Component = 'div',
      display,
      flexDirection,
      alignItems,
      justifyContent,
      flexWrap,
      flexGrow,
      flexShrink,
      gap,
      width,
      height,
      maxWidth,
      minWidth,
      maxHeight,
      minHeight,
      overflow,
      position,
      textAlign,
      alignSelf,
      noValidate,
      m,
      mt,
      mb,
      ml,
      mr,
      mx,
      my,
      p,
      pt,
      pb,
      pl,
      pr,
      px,
      py,
      className,
      style,
      ...props
    },
    ref,
  ) => (
    <Component
      ref={ref}
      className={cn(className)}
      noValidate={noValidate}
      style={definedStyles({
        display,
        flexDirection,
        alignItems,
        justifyContent,
        flexWrap,
        flexGrow,
        flexShrink,
        gap: space(gap),
        width,
        height,
        maxWidth,
        minWidth,
        maxHeight,
        minHeight,
        overflow,
        position,
        textAlign,
        alignSelf,
        margin: space(m),
        marginTop: space(mt ?? my),
        marginBottom: space(mb ?? my),
        marginLeft: space(ml ?? mx),
        marginRight: space(mr ?? mx),
        padding: space(p),
        paddingTop: space(pt ?? py),
        paddingBottom: space(pb ?? py),
        paddingLeft: space(pl ?? px),
        paddingRight: space(pr ?? px),
        ...style,
      })}
      {...props}
    />
  ),
)
Box.displayName = 'Box'

export default Box
